import { createRouter, createWebHistory } from 'vue-router'
import { useInstanceStore } from '@/store/backendInstances'
import { useLogsStore } from '@/store/logs'
import BackendAPI from '@/BackendAPI.js'
import { useMiscellaneousStore } from '@/store/miscellaneous.js'
import { useDisplayModeStore } from '@/store/displayMode.ts'
import { isDefaultDashboard, prebuiltDashboards } from '@/composables/dashboards/DefaultDashboards.ts'

const routes = [
  {
    path: '/',
    children: [
      {
        path: '/logs',
        name: 'Logs',
        component: () => import('@/views/LogViewer.vue'),
      },
      {
        path: '',
        name: 'Home',
        // redirect to instance dashboard if there is only 1 instance available
        redirect: () => {
          const instanceStore = useInstanceStore()

          const onlyBarnOrDataCollectorInstances = Object.values(instanceStore.instances).filter(
            instance => instance.instanceType === 'barn' || instance.instanceType === 'data_collector'
          )
          if (onlyBarnOrDataCollectorInstances.length === 1 && instanceStore.isLoggedIn(onlyBarnOrDataCollectorInstances[0].globalUniqueIdentifier)) {
            return {
              name: 'InstanceDashboard',
              params: {
                globalUniqueIdentifier: onlyBarnOrDataCollectorInstances[0].globalUniqueIdentifier,
              },
            }
          }
          return { name: 'Instances' }
        },
      },
      {
        path: '/instances',
        name: 'Instances',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "home" */ '@/components/InstanceList.vue'),
        meta: {
          backButton: false,
        },
      },
      {
        path: '/global-incidents',
        name: 'GlobalIncidents',
        component: () => import('@/views/GlobalIncidents.vue'),
      },
      {
        path: '/overview',
        name: 'Quick Instance Overview',
        component: () => import('@/views/QuickInstanceOverview.vue'),
        meta: {
          backButton: true,
        },
      },
      {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/LocalSettings.vue'),
        meta: {
          title: 'router_elements.settings',
          backButton: true,
        },
      },
      {
        path: '/ssoAuthentication',
        name: 'Senator Authentication',
        component: () => import('@/views/SenatorAuthentication.vue'),
      },
      {
        path: '/addInstance',
        children: [
          {
            path: '',
            name: 'Add Instance',
            component: () => import('@/views/AddInstance.vue'),
          },
          {
            path: 'new',
            name: 'Create Instance',
            component: () => import('@/views/CreateInstance.vue'),
          },
          {
            path: 'sso',
            name: 'Single Sign On',
            children: [
              {
                path: 'serviceProviders',
                name: 'Select ServiceProvider',
                component: () => import('@/views/SelectServiceProviders.vue'),
              },
            ],
          },
        ],
      },
      {
        path: '/ql',
        children: [
          {
            path: 'email-verification',
            component: () => import('@/views/quickLinks/EmailVerification.vue'),
            name: 'Email Verification',
          },
          {
            path: 'account-provisioning',
            component: () => import('@/views/quickLinks/SetPassword.vue'),
            name: 'Account Provisioning',
          },
        ],
      },
      {
        path: '/admiral/:globalUniqueIdentifier/',
        component: () => import('@/views/AdmiralDashboard.vue'),
        name: 'AdmiralDashboard',
      },
      {
        path: '/instance/:globalUniqueIdentifier',
        alias: ['/data-collector/:globalUniqueIdentifier'],
        component: () => import('@/views/InstanceDashboard.vue'),
        name: 'InstanceDashboard',
        redirect: to => {
          const instanceStore = useInstanceStore()
          const instance = instanceStore.instances[to.params.globalUniqueIdentifier]
          if (instance.instanceType === 'barn') {
            const dashboardKey = useDisplayModeStore().getDisplayMode(to.params.globalUniqueIdentifier)?.startDashboard
            if (dashboardKey) {
              if (isDefaultDashboard(dashboardKey)) {
                return prebuiltDashboards[dashboardKey].navigationItem.to
              } else {
                return { name: 'Dashboard', params: { dashboardId: dashboardKey, globalUniqueIdentifier: to.params.globalUniqueIdentifier } }
              }
            }
            return { name: 'Dashboard Overview', params: { globalUniqueIdentifier: to.params.globalUniqueIdentifier } }
          }
          if (instance.instanceType === 'senator')
            return { name: 'Senator Overview', params: { globalUniqueIdentifier: to.params.globalUniqueIdentifier } }
          if (instance.instanceType === 'admiral')
            return { name: 'AdmiralDashboard', params: { globalUniqueIdentifier: to.params.globalUniqueIdentifier } }
          if (instance.instanceType === 'data_collector')
            return { name: 'DataCollectorOverview', params: { globalUniqueIdentifier: to.params.globalUniqueIdentifier } }
          return { name: 'Config' }
        },
        children: [
          {
            path: 'ops',
            name: 'Ops Dashboard',
            component: () => import('@/views/OpsDashboard.vue'),
            meta: {
              title: 'router_elements.ops_dashboard',
              backButton: false,
            },
          },
          {
            path: 'settings',
            name: 'Specific Settings',
            component: () => import('@/views/LocalSettings.vue'),
            props: true,
            meta: {
              title: 'router_elements.settings',
              backButton: true,
            },
          },
          {
            path: 'data-collector',
            name: 'DataCollectorOverview',
            component: () => import('@/views/data-collector/DataCollectorDashboard.vue'),
            meta: {
              title: 'router_elements.data_collector.overview',
              backButton: true,
            },
          },
          {
            name: 'DataCollectorFileUpload',
            path: 'data-collector/file-upload',
            component: () => import('@/views/data-collector/DataCollectorFileUpload.vue'),
            meta: {
              title: 'router_elements.data_collector.file_upload',
              backButton: true,
            },
          },
          {
            name: 'ProjectStatistics',
            path: 'data-collector/project-statistics',
            component: () => import('@/views/data-collector/ProjectStatistics.vue'),
            meta: {
              title: 'router_elements.data_collector.project_statistics',
              backButton: true,
            },
          },
          {
            name: 'Data Collector AnnotationList',
            path: 'data-collector/:recordId/annotate/:annotationId',
            props: true,
            component: () => import('@/views/data-collector/DataCollectorAnnotate.vue'),
            meta: {
              title: 'router_elements.data_collector.annotate',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            name: 'Data Collector Step Through Annotations',
            path: 'data-collector/annotations-by-annotator/:accountId',
            props: true,
            component: () => import('@/views/data-collector/StepThroughAnnotations.vue'),
            meta: {
              title: 'router_elements.data_collector.annotations_by_annotator',
              backButton: true,
            },
          },
          {
            name: 'Data Collector Annotate',
            path: 'data-collector/:recordId/annotate',
            props: true,
            component: () => import('@/views/data-collector/DataCollectorAnnotate.vue'),
            meta: {
              title: 'router_elements.data_collector.annotate',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            name: 'Data Collector Annotate Next',
            path: 'data-collector/next',
            component: () => import('@/views/data-collector/DataCollectorAnnotateNext.vue'),
            meta: {
              title: 'router_elements.data_collector.annotate_next',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            name: 'Data Collector Annotations',
            path: 'data-collector/annotations',
            component: () => import('@/views/data-collector/AnnotationList.vue'),
            meta: {
              title: 'router_elements.data_collector.annotations',
              backButton: true,
            },
          },
          {
            name: 'DataCollector Records',
            path: 'data-collector/records',
            component: () => import('@/views/data-collector/DataCollectorRecords.vue'),
            meta: {
              title: 'router_elements.data_collector.records',
              backButton: true,
            },
          },
          {
            name: 'Data Collector Record',
            path: 'data-collector/records/:recordId',
            props: true,
            component: () => import('@/views/data-collector/DataCollectorRecordView.vue'),
            meta: {
              title: 'router_elements.data_collector.record',
              backButton: true,
            },
          },
          {
            path: 'server-management/:guid',
            name: 'Server Management',
            component: () => import('@/views/ServerManagement.vue'),
            meta: {
              title: 'router_elements.server_management',
              backButton: true,
            },
            props: true,
          },
          {
            path: 'authorization-management',
            name: 'Authorization Management',
            component: () => import('@/views/AuthorizationManagement.vue'),
            meta: {
              title: 'router_elements.authorization_management',
              backButton: true,
            },
          },
          {
            path: 'vms-instances',
            name: 'VMSInstanceList',
            component: () => import('@/views/VMSInstanceList.vue'),
            meta: {
              title: 'router_elements.vms_instances',
              backButton: false,
            },
          },
          {
            path: 'admiral-dashboard',
            component: () => import('@/views/AdmiralDashboard.vue'),
            name: 'AdmiralDashboard',
            meta: {
              title: 'Dashboard',
              backButton: false,
            },
          },
          {
            path: 'vms-instances-contract-information',
            name: 'VmsInstancesContractInformation',
            component: () => import('@/views/VmsInstancesContractInformation.vue'),
            meta: {
              title: 'router_elements.contract_information',
              backButton: false,
            },
          },
          {
            path: 'senator',
            name: 'Senator Overview',
            component: () => import('@/views/SenatorOverview.vue'),
            meta: {
              title: 'router_elements.senator_overview',
              backButton: false,
            },
          },
          {
            path: 'config',
            name: 'Config',
            component: () => import('@/views/VMSConfig.vue'),
            meta: {
              title: 'router_elements.config',
              backButton: true,
            },
          },
          {
            path: 'chart-sharing-creator',
            name: 'Chart Sharing Creator',
            component: () => import('@/views/ChartSharingCreator.vue'),
            props: true,
            meta: {
              title: 'router_elements.charts_sharing',
              backButton: true,
            },
          },
          {
            path: 'charts',
            name: 'Charts',
            component: () => import('@/views/ChartSharing.vue'),
            props: true,
            meta: {
              title: 'router_elements.charts',
              backButton: true,
            },
          },
          {
            path: 'weights',
            name: 'Weights',
            component: () => import('@/views/WeightData.vue'),
            meta: {
              title: 'Standard Weights',
              backButton: true,
            },
          },
          {
            path: 'dashboards',
            children: [
              {
                path: 'barn-management',
                name: 'Barn Management Dashboard',
                component: () => import('@/views/dashboards/BarnManagementDashboard.vue'),
                meta: {
                  title: 'router_elements.barn_management',
                  backButton: true,
                  temporaryNavigationDrawer: true,
                },
              },
              {
                path: 'fattening-overview',
                name: 'Fattening Overview',
                component: () => import('@/views/dashboards/FatteningDashboard.vue'),
                meta: {
                  title: 'router_elements.fattening_overview',
                  backButton: true,
                  temporaryNavigationDrawer: true,
                },
              },
              {
                path: 'region-overview',
                name: 'Region Overview',
                component: () => import('@/views/dashboards/RegionOverview.vue'),
                meta: {
                  title: 'router_elements.region_overview',
                  backButton: true,
                  temporaryNavigationDrawer: true,
                },
              },
              {
                path: 'measurements',
                name: 'Measurements Dashboard',
                component: () => import('@/views/dashboards/MeasurementsDashboard.vue'),
                meta: {
                  title: 'router_elements.measurement_dashboard',
                  backButton: true,
                  temporaryNavigationDrawer: false,
                },
              },
              {
                path: 'dead-animals',
                name: 'Dead Animals Dashboard',
                component: () => import('@/views/dashboards/DeadAnimalStatisticsDashboard.vue'),
                meta: {
                  title: 'router_elements.dead_animals',
                  backButton: true,
                },
              },
              {
                path: 'quick-artifacts',
                name: 'Quick Artifact Dashboard',
                component: () => import('@/views/dashboards/QuickArtifactDashboard.vue'),
                meta: {
                  title: 'router_elements.quick_artifacts',
                  backButton: true,
                },
              },
              {
                path: ':dashboardId',
                name: 'Dashboard',
                component: () => import('@/views/CustomDashboard.vue'),
                meta: {
                  title: 'router_elements.dashboard',
                  backButton: true,
                },
                props: true,
              },
            ],
          },
          {
            path: 'canvas-editor/:cameraCanvasId?/:duplicate?',
            name: 'Canvas Editor',
            component: () => import('@/views/CanvasEditor.vue'),
            props: true,
            meta: {
              title: 'router_elements.canvas_editor',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            path: 'network-management',
            name: 'Network Management',
            component: () => import('@/views/NetworkManagement.vue'),
            props: true,
            meta: {
              title: 'router_elements.network_management',
              backButton: true,
              temporaryNavigationDrawer: false,
            },
          },
          {
            path: 'cameras',
            name: 'CameraList',
            component: () => import('@/views/CameraList.vue'),
            meta: {
              title: 'router_elements.cameras',
              backButton: true,
              disableDefaultAppBar: true,
            },
          },
          {
            path: 'cameras/:cameraId',
            name: 'CameraView',
            component: () => import('@/views/CameraView.vue'),
            meta: {
              title: 'router_elements.camera_view',
              backButton: true,
            },
            props: true,
          },
          {
            path: 'networkDiscovery',
            name: 'Network Discovery',
            component: () => import('@/views/NetworkDiscovery.vue'),
            meta: {
              title: 'router_elements.network_discovery',
              backButton: true,
            },
          },
          {
            path: 'live/:cameraId',
            name: 'Live Stream',
            props: true,
            component: () => import('@/views/CameraLiveStream.vue'),
            meta: {
              title: 'router_elements.live_stream',
              backButton: true,
            },
          },
          {
            path: 'cameraCanvases',
            name: 'canvasList',
            component: () => import('@/views/CanvasList.vue'),
            meta: {
              title: 'router_elements.canvas_list',
              backButton: true,
            },
          },
          {
            path: 'canvas/:cameraCanvasId?',
            name: 'CameraCanvas',
            props: true,
            component: () => import('@/views/CameraCanvas.vue'),
            meta: {
              title: 'router_elements.canvas',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            path: 'interventions',
            name: 'BarnInterventions',
            component: () => import('@/views/BarnInterventions.vue'),
            meta: {
              title: 'router_elements.interventions',
              backButton: true,
            },
          },
          {
            path: 'wysiwyg',
            name: 'WYSIWYG Editor',
            component: () => import('@/views/WYSIWYG-Playground.vue'),
            meta: {
              title: 'router_elements.wysiwyg_editor',
              backButton: true,
            },
          },
          {
            path: 'documentation',
            name: 'Barn Documentation',
            component: () => import('@/views/BarnDocumentationList.vue'),
            meta: {
              title: 'router_elements.documentation',
              backButton: true,
            },
          },
          {
            path: 'frameSeek/:prePickedCamera?',
            name: 'Frame Seek',
            props: true,
            component: () => import('@/views/FrameSeek.vue'),
            meta: {
              title: 'router_elements.frame_seek',
              backButton: true,
            },
          },
          {
            path: 'measurements/:cameraId',
            name: 'Camera Measurements',
            props: true,
            component: () => import('@/views/CameraMeasurements.vue'),
            meta: {
              title: 'router_elements.camera_measurements',
              backButton: true,
            },
          },
          {
            path: 'my-incidents',
            name: 'My Incidents',
            component: () => import('@/views/MyIncidents.vue'),
            meta: {
              title: 'router_elements.my_incidents',
              backButton: true,
            },
          },
          {
            path: 'incidents',
            name: 'Incidents',
            component: () => import('@/views/IncidentList.vue'),
            meta: {
              title: 'router_elements.incidents',
              backButton: true,
            },
          },
          {
            path: 'recordings',
            name: 'Recordings',
            component: () => import('@/views/RecordingList.vue'),
            meta: {
              title: 'router_elements.recordings',
              backButton: true,
            },
          },
          {
            path: 'stream/:selectorMode/:selectorId/:timestamp?',
            name: 'RecordingStream',
            props: true,
            component: () => import('@/views/RecordingStream.vue'),
            meta: {
              title: 'router_elements.recording_stream',
              backButton: true,
            },
          },
          {
            path: 'region-drawing/:cameraId',
            name: 'Region Drawing',
            props: true,
            component: () => import('@/views/RegionDrawing.vue'),
            meta: {
              title: 'router_elements.region_drawing',
              backButton: true,
              temporaryNavigationDrawer: true,
            },
          },
          {
            path: 'region-editor',
            name: 'Region Editor',
            component: () => import('@/views/RegionManagement.vue'),
            meta: {
              title: 'router_elements.region_editor',
              backButton: true,
            },
          },
          {
            path: 'artifacts',
            name: 'Artifacts',
            component: () => import('@/views/ArtifactList.vue'),
            meta: {
              title: 'router_elements.artifacts',
              backButton: true,
            },
          },
          {
            path: 'artifact/:artifactId',
            name: 'Artifact',
            props: true,
            component: () => import('@/views/ArtifactView.vue'),
            meta: {
              title: 'router_elements.artifact',
              backButton: true,
            },
          },
          {
            path: 'display-mode-management',
            name: 'Display Mode Management',
            component: () => import('@/views/DisplayModeManagement.vue'),
            meta: {
              backButton: true,
              title: 'router_elements.display_mode-management',
            },
          },
          {
            path: 'incident/:incidentId',
            name: 'IncidentView',
            props: true,
            component: () => import('@/views/IncidentView.vue'),
            meta: {
              title: 'router_elements.incident',
              backButton: true,
            },
          },
          {
            path: 'recording-export',
            name: 'Recording Export',
            component: () => import('@/views/RecordingExport.vue'),
            meta: {
              title: 'router_elements.recording_export',
              backButton: true,
            },
          },
          {
            path: 'camera-overview',
            name: 'Camera Overview',
            component: () => import('@/views/CameraOverview.vue'),
            meta: {
              title: 'router_elements.camera_overview',
              backButton: true,
            },
          },
          {
            path: 'recording-saving',
            name: 'Recording Saving',
            component: () => import('@/views/RecordingSaving.vue'),
            meta: {
              title: 'router_elements.recording_saving',
            },
          },
          {
            path: 'eulas/:accountId?',
            name: 'Eulas',
            props: true,
            component: () => import('@/views/EulaList.vue'),
            meta: {
              title: 'router_elements.eulas',
              backButton: true,
            },
          },
          {
            path: 'licenses',
            name: 'Licenses',
            component: () => import('@/views/OpenSourceLicenses.vue'),
            meta: {
              title: 'router_elements.licenses',
              backButton: true,
            },
          },
          {
            path: 'epoch-overview',
            name: 'Epoch Overview',
            component: () => import('@/views/EpochOverview.vue'),
            meta: {
              title: 'router_elements.epoch_overview',
              backButton: true,
            },
          },
          {
            path: 'dashboard-list',
            name: 'Dashboards',
            component: () => import('@/views/DashboardList.vue'),
            meta: {
              title: 'router_elements.dashboards',
              backButton: true,
            },
          },
          {
            path: 'dashboard-editor/:dashboardId?',
            name: 'Dashboard Editor',
            component: () => import('@/views/DashboardEditor.vue'),
            meta: {
              title: 'router_elements.dashboard_editor',
              backButton: true,
            },
            props: true,
          },
          {
            path: 'dashboard-overview',
            name: 'Dashboard Overview',
            component: () => import('@/views/DashboardOverviewPage.vue'),
            meta: {
              title: 'router_elements.dashboard_overview',
              backButton: true,
            },
          },
          {
            path: 'more',
            name: 'More',
            component: () => import('@/views/MoreNavItemsMobile.vue'),
            meta: {
              title: 'router_elements.more',
              backButton: true,
            },
            props: true,
          },
          {
            path: 'pen-observation',
            name: 'Pen Observation',
            component: () => import('@/views/PenObservations.vue'),
            meta: {
              title: 'router_elements.pen_observations',
              backButton: false,
            },
          },
          {
            path: 'report-setup',
            name: 'Report Setup',
            component: () => import('@/views/ReportSetup.vue'),
            meta: {
              title: 'router_elements.report_setup',
              backButton: false,
            },
          },
          {
            path: 'llm/generate',
            name: 'LLM Generate',
            component: () => import('@/views/llm/Generate.vue'),
            meta: {
              title: 'router_elements.llm.generate',
              backButton: false,
            },
          },
          {
            path: 'llm/system-prompts',
            name: 'LLM System Prompts',
            component: () => import('@/views/llm/SystemPromptManagement.vue'),
            meta: {
              title: 'router_elements.llm.system_prompts',
              backButton: false,
            },
          },
          {
            path: 'llm/chats',
            name: 'LLM Chats',
            component: () => import('@/views/llm/MyChats.vue'),
            meta: {
              title: 'router_elements.llm.chats',
              backButton: false,
            },
          },
          {
            path: 'llm/chats/new',
            name: 'LLM New Chat',
            component: () => import('@/views/llm/ChatConversation.vue'),
            meta: {
              title: 'router_elements.llm.chat',
              backButton: true,
            },
          },
          {
            path: 'llm/chats/:llmChatId',
            name: 'LLM Chat',
            component: () => import('@/views/llm/ChatConversation.vue'),
            props: true,
            meta: {
              title: 'router_elements.llm.chat',
              backButton: true,
            },
          },
          {
            path: 'llm/tools',
            name: 'LLM Tools',
            component: () => import('@/views/llm/ToolExecution.vue'),
            meta: {
              title: 'router_elements.llm.tools',
              backButton: false,
            },
          },
          {
            path: 'llm/personal-facts',
            name: 'LLM Personal Facts',
            component: () => import('@/views/llm/PersonalFacts.vue'),
            meta: {
              title: 'router_elements.llm.personal_facts',
              backButton: false,
            },
          },
          {
            path: 'rag/indexed-documents',
            name: 'RAG Indexed Documents',
            component: () => import('@/views/llm/IndexedDocuments.vue'),
            meta: {
              title: 'router_elements.rag.indexed_documents',
              backButton: false,
            },
          },
          {
            path: 'rag/upload',
            name: 'RAG Upload',
            component: () => import('@/views/llm/IndexedDocumentUpload.vue'),
            meta: {
              title: 'router_elements.rag.upload',
              backButton: false,
            },
          },
          {
            path: 'rag/search',
            name: 'RAG Search',
            component: () => import('@/views/llm/RagSearch.vue'),
            meta: {
              title: 'router_elements.rag.search',
              backButton: false,
            },
          },
          {
            path: 'reports',
            name: 'Reports',
            component: () => import('@/views/Reports.vue'),
            meta: {
              title: 'router_elements.reports',
              backButton: true,
            },
          },
          {
            path: 'create-report/:reportTemplateId?',
            name: 'Create Report',
            component: () => import('@/views/CreateReport.vue'),
            props: true,
            meta: {
              title: 'router_elements.create_report',
              backButton: true,
            },
          },
          {
            path: 'report/:reportId',
            name: 'Report',
            component: () => import('@/views/ReportViewer.vue'),
            props: true,
            meta: {
              title: 'router_elements.report',
              backButton: true,
            },
          },
          {
            path: 'minimal-report/:reportId',
            name: 'Minimal Report',
            component: () => import('@/views/MinimalReportViewer.vue'),
            props: true,
            meta: {
              title: 'router_elements.report',
              backButton: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from) {
    const disableScrollResetRoutes = ['Frame Seek', 'IncidentView']
    if (disableScrollResetRoutes.includes(to.name)) {
      return {}
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    if (from !== to) {
      return { top: 0, behavior: 'smooth' }
    }
    return {}
  },
})

router.beforeEach((to, from, next) => {
  if (to?.params && Object.keys(to.params).includes('globalUniqueIdentifier')) {
    try {
      BackendAPI.getOrCreateBackendAPIInstance(to.params.globalUniqueIdentifier).updateLastUserInteractionTimestamp()
    } catch {}
  }
  useLogsStore().addLogEntry({ message: `Navigating to ${to.name}, ${to.fullPath}`, tag: 'router', level: 'INFO' })
  BackendAPI.triggerRouteChange(from.path, to.path)
  router.from = from
  if (to.meta.temporaryNavigationDrawer) {
    useMiscellaneousStore().expandInstanceDashboardNavigationDrawer = false
  }
  if (!to.meta.temporaryNavigationDrawer) {
    useMiscellaneousStore().expandInstanceDashboardNavigationDrawer = true
  }
  next()
})

export default router
